import { useEffect, useMemo, useState } from "react";
import { YStack, XStack, Text } from "tamagui";
import { colors } from "@styles/colors";
import { captureSmsInviteFromUrl } from "@utils/smsInvite";
import {
  parseSmsInviteOpenParams,
  buildSmsInviteLiffUrl,
  buildSmsInviteBrowserPath,
  shouldAutoOpenLiff,
  markSmsInviteAutoOpened,
} from "@utils/smsInviteOpen";
import { trackGa } from "@lib/tracker";

/**
 * 簡訊邀請中繼頁 `/sms-invite/open`。
 *
 * 簡訊只能放自有網域（電信擋 liff.line.me），本頁負責把客戶帶進瓦斯行自己的 LIFF：
 *   - 進站先把 phone/invite/order_id/src 存進 sessionStorage（跨流程攜帶，與 /sms-invite 相同）
 *   - 自動嘗試導向 `https://liff.line.me/{liffId}/sms-invite?…`（手機系統多半不會因程式導向
 *     喚起 LINE app，所以畫面同時給一顆按鈕，使用者親手點才會穩定開 LINE）
 *   - 「在瀏覽器繼續」退回本站 `/sms-invite?liffId=…`（電腦、沒裝 LINE、LINE 開不起來）
 *   - 簡訊沒帶 liffId（瓦斯行沒設 liff_main_id）→ 沒有 LIFF 連結可導，直接走本站落地頁
 *
 * 本頁在 LineLoginProvider 內是旁路（isSmsInviteOpenMode）：不初始化 LIFF、不導登入。
 */
const SmsInviteOpen = () => {
  const params = useMemo(() => parseSmsInviteOpenParams(), []);
  const liffUrl = useMemo(() => (params ? buildSmsInviteLiffUrl(params) : null), [params]);
  const browserPath = useMemo(() => (params ? buildSmsInviteBrowserPath(params) : "/sms-invite"), [params]);
  const [autoTried, setAutoTried] = useState(false);

  useEffect(() => {
    // 與 /sms-invite 相同：先把參數存起來，之後不論走哪條路都拿得到
    captureSmsInviteFromUrl();

    if (!params) {
      // 參數不完整：交給落地頁處理（它會導去序號註冊保底）
      trackGa("funnel_step", { funnel: "sms_invite", step: "open_relay", result: "invalid_params" });
      window.location.replace("/sms-invite");
      return;
    }
    if (!liffUrl) {
      // 沒有瓦斯行 LIFF ID → 沒有東西可以導，直接走本站落地頁（預設 LIFF）
      trackGa("funnel_step", { funnel: "sms_invite", step: "open_relay", result: "no_liff_id" });
      window.location.replace(browserPath);
      return;
    }
    trackGa("funnel_step", { funnel: "sms_invite", step: "open_relay", result: "shown" });
    if (shouldAutoOpenLiff()) {
      markSmsInviteAutoOpened(params.invite);
      setAutoTried(true);
      window.location.href = liffUrl;
    }
  }, [params, liffUrl, browserPath]);

  if (!params || !liffUrl) return null;

  return (
    <YStack f={1} width="100vw" height="100dvh" ai="center" jc="center" bg={colors.bg} px="$6" gap="$5">
      <YStack ai="center" gap="$2">
        <Text fontSize={20} fontWeight="700" color={colors.grayDark} textAlign="center">
          在 LINE 中完成註冊
        </Text>
        <Text fontSize={14} color={colors.gray} textAlign="center" lineHeight={22}>
          {autoTried ? "如果 LINE 沒有自動打開，請點下方按鈕。" : "請點下方按鈕，用 LINE 開啟邀請。"}
        </Text>
      </YStack>

      <a
        href={liffUrl}
        onClick={() => trackGa("funnel_step", { funnel: "sms_invite", step: "open_relay", result: "tap_open_line" })}
        style={{ textDecoration: "none", width: "100%", maxWidth: 360 }}
      >
        <XStack
          ai="center"
          jc="center"
          bg="#06C755"
          br={12}
          py="$4"
          px="$5"
          gap="$2"
          pressStyle={{ opacity: 0.85 }}
        >
          <Text fontSize={17} fontWeight="700" color={colors.light}>
            在 LINE 中開啟
          </Text>
        </XStack>
      </a>

      <a
        href={browserPath}
        onClick={() => trackGa("funnel_step", { funnel: "sms_invite", step: "open_relay", result: "tap_browser" })}
        style={{ textDecoration: "none" }}
      >
        <Text fontSize={13} color={colors.primary} textAlign="center">
          沒有安裝 LINE？在瀏覽器繼續
        </Text>
      </a>
    </YStack>
  );
};

export default SmsInviteOpen;
